import {
  Box,
  Divider,
  Heading,
  HeadingProps,
  HStack,
  Tab as ChakraTab,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router";

import ErrorBoundary from "@/common/components/ErrorBoundary";
import HeaderLayout from "@/common/components/HeaderLayout";
import {
  IcoApp,
  IcoArchive,
  IcoAutoFeedbackSurveys,
  IcoBilling,
  IcoEnvironments,
  IcoExperiments,
  IcoExport,
  IcoFeatureView,
  IcoImport,
  IcoIntegrations,
  IcoKey,
  IcoNotification,
  IcoOrganization,
  IcoSegment,
  IcoStages,
  IcoTeam,
} from "@/common/components/Icons";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { APIAccessManagement } from "@/global-settings/components/APIAccessManagement";
import { ArchivingFlow } from "@/global-settings/components/ArchivingFlow";
import Environments from "@/global-settings/components/Environments";
import Experiments from "@/global-settings/components/Experiments";
import FeatureViews from "@/global-settings/components/FeatureViews";
import FeedbackPrompting from "@/global-settings/components/FeedbackPrompting";
import GeneralApp from "@/global-settings/components/GeneralApp";
import GeneralOrganization from "@/global-settings/components/GeneralOrganization";
import ManageData from "@/global-settings/components/ManageData";
import Notifications from "@/global-settings/components/Notifications";
import OrganizationBilling from "@/global-settings/components/OrgBilling";
import Segments from "@/global-settings/components/Segments";
import Stages from "@/global-settings/components/Stages";
import Team from "@/global-settings/components/Team";
import { ImportTools } from "@/integration/components/ImportTools";
import Integrations from "@/integration/components/Integrations";
import { useFlag } from "@reflag/react-sdk";
import { GlobalSettingsSections } from "@reflag/shared/urls";

function TabSectionHeading({
  children,
  ...props
}: { children: React.ReactNode } & HeadingProps) {
  return (
    <Heading
      as="h3"
      fontWeight="medium"
      mb={3}
      mt={2}
      pl={3}
      size="xs"
      {...props}
    >
      {children}
    </Heading>
  );
}

function Tab({
  children,
  to,
  ...props
}: { children: React.ReactNode; to: string } & TabProps) {
  return (
    <ChakraTab as={Link} to={to} {...props}>
      {children}
    </ChakraTab>
  );
}

type Route = {
  to: GlobalSettingsSections;
  name: string;
  icon: JSX.Element;
  panel: JSX.Element;
  meta?: Record<string, any>;
};

const orgRoutes: Route[] = [
  {
    name: "General",
    to: "org-general",
    icon: <IcoOrganization size="lg" />,
    panel: <GeneralOrganization />,
  },
  {
    name: "Team",
    to: "org-team",
    icon: <IcoTeam size="lg" />,
    panel: <Team />,
  },
  {
    name: "Experiments",
    to: "org-experiments",
    icon: <IcoExperiments size="lg" />,
    panel: <Experiments />,
  },
  {
    name: "Integrations",
    to: "org-integrations",
    icon: <IcoIntegrations size="lg" />,
    panel: <Integrations />,
  },
  {
    name: "Billing",
    to: "org-billing",
    icon: <IcoBilling size="lg" />,
    panel: <OrganizationBilling />,
  },
  {
    name: "Cleanup & Archive",
    to: "org-archiving-flow",
    icon: <IcoArchive size="lg" />,
    panel: <ArchivingFlow />,
  },
];

const appRoutes: Route[] = [
  {
    name: "General",
    to: "app-general",
    icon: <IcoApp size="lg" />,
    panel: <GeneralApp />,
  },
  {
    name: "SDK & API keys",
    to: "org-api-access",
    icon: <IcoKey size="lg" />,
    panel: <APIAccessManagement />,
  },
  {
    name: "Segments",
    to: "app-segments",
    icon: <IcoSegment size="lg" />,
    panel: <Segments />,
  },
  {
    name: "Environments",
    to: "app-environments",
    icon: <IcoEnvironments size="lg" />,
    panel: <Environments />,
  },
  {
    name: "Stages",
    to: "app-stages",
    icon: <IcoStages size="lg" />,
    panel: <Stages />,
  },
  {
    name: "Views",
    to: "app-feature-views",
    icon: <IcoFeatureView size="lg" />,
    panel: <FeatureViews />,
  },
  {
    name: "Notification settings",
    to: "app-notifications",
    icon: <IcoNotification size="lg" />,
    panel: <Notifications />,
  },
  {
    name: "Manage data",
    to: "app-manage-data",
    icon: <IcoExport size="lg" />,
    panel: <ManageData />,
  },
  {
    name: "Import",
    to: "app-import",
    icon: <IcoImport size="lg" />,
    panel: <ImportTools />,
  },
];

const environmentRoutes: Route[] = [
  {
    name: "Feedback",
    to: "app-feedback",
    icon: <IcoAutoFeedbackSurveys size="lg" />,
    panel: <FeedbackPrompting />,
  },
];

export default function GlobalSettings() {
  const navigate = useNavigate();
  const { app, env } = useEnvironment();
  const { section = "org-general" } = useParams() as {
    section: GlobalSettingsSections;
  };
  const { isEnabled: launchDarklyImportEnabled } = useFlag(
    "launchdarkly-import",
  );
  const { isEnabled: flagsmithExportEnabled } = useFlag("flagsmith-export");
  const { isEnabled: archivingEnabled } = useFlag("archiving");

  const ignoredRoutes: string[] = [];

  if (!launchDarklyImportEnabled && !flagsmithExportEnabled) {
    ignoredRoutes.push("app-import");
  }
  if (!archivingEnabled) ignoredRoutes.push("org-archiving-flow");

  const filteredAppRoutes = appRoutes.filter(
    (r) => !ignoredRoutes.includes(r.to),
  );
  const filteredOrgRoutes = orgRoutes.filter(
    (r) => !ignoredRoutes.includes(r.to),
  );
  const filteredEnvironmentRoutes = environmentRoutes.filter(
    (r) => !ignoredRoutes.includes(r.to),
  );

  const allRoutes = [
    ...filteredOrgRoutes,
    ...filteredAppRoutes,
    ...filteredEnvironmentRoutes,
  ];
  const sections = allRoutes.map((r) => r.to);

  const currentSectionIndex = sections.indexOf(section);
  const currentRoute = allRoutes.find((r) => r.to === section);

  let sectionGroup = "Organization";
  if (currentRoute && appRoutes.includes(currentRoute)) {
    sectionGroup = "App";
  } else if (currentRoute && environmentRoutes.includes(currentRoute)) {
    sectionGroup = "Environment";
    if (currentRoute.meta?.onlyProduction && !env.isProduction) {
      navigate(`../app-feedback`);
    }
  }

  return (
    <HeaderLayout title="Settings">
      <Helmet>
        <title>
          Settings › {sectionGroup} › {currentRoute?.name ?? ""}
        </title>
      </Helmet>
      {/*
        This extra wrapper is needed to have Safari not be confused about the height.
        If you put the `flexGrow` on the `Tabs` container, Safari will size Tabs grid-children
        taller than the Tabs grid-container, causing higher than viewport content.
      */}
      <Box display="flex" flexGrow={1}>
        <Tabs
          index={currentSectionIndex}
          orientation="vertical"
          variant="sideMenu"
          w="100%"
          isLazy
          isManual
        >
          <TabList>
            <TabSectionHeading>Organization</TabSectionHeading>
            {filteredOrgRoutes.map((route) => (
              <Tab key={route.to} to={`../${route.to}`}>
                <HStack>
                  {route.icon}
                  <Text>{route.name}</Text>
                </HStack>
              </Tab>
            ))}
            <Divider my={2} />
            <TabSectionHeading mt={4}>App: {app.name}</TabSectionHeading>
            {filteredAppRoutes.map((route) => (
              <Tab key={route.to} to={`../${route.to}`}>
                <HStack>
                  {route.icon}
                  <Text>{route.name}</Text>
                </HStack>
              </Tab>
            ))}
            <Divider my={2} />
            <TabSectionHeading mt={4}>
              Environment: {env.name}
            </TabSectionHeading>
            {filteredEnvironmentRoutes.map((route) => (
              <Tab
                key={route.to}
                isDisabled={route.meta?.onlyProduction && !env.isProduction}
                to={`../${route.to}`}
              >
                <HStack>
                  {route.icon}
                  <Text>{route.name}</Text>
                </HStack>
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {allRoutes.map((route) => (
              <TabPanel key={route.to}>
                <ErrorBoundary>{route.panel}</ErrorBoundary>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>
    </HeaderLayout>
  );
}
